import { useRef, useState } from "react";
import api, { errMsg, API_BASE_URL } from "../lib/api";
import { useToast } from "../lib/toast";
import { Modal, Button, cx } from "./ui";
import {
  SettingsIcon,
  UploadIcon,
  FileIcon,
  DownloadIcon,
  TrashIcon,
  SparkIcon,
  CheckIcon,
} from "./ui/Icons";

export default function AccountMappingModal({ onClose, fileId }) {
  const [file, setFile] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [mapped, setMapped] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const toast = useToast();

  const handleUpload = async () => {
    if (!file) {
      toast.error("No file selected", "Choose an Excel file first.");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("fileId", fileId);
    try {
      setUploading(true);
      await api.post("/api/excel/accounts/upload", form);
      setUploaded(true);
      setMapped(false);
      toast.success("Excel uploaded", "Now run the mapping step.");
    } catch (err) {
      toast.error("Upload failed", errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const handleMapping = async () => {
    try {
      setMapping(true);
      await api.post("/api/mapping/accounts/map", { fileId });
      setMapped(true);
      toast.success("Mapping complete", "You can download the results now.");
    } catch (err) {
      toast.error("Mapping failed", errMsg(err));
    } finally {
      setMapping(false);
    }
  };

  const downloadExcel = (kind) => {
    window.open(
      `${API_BASE_URL}/api/export/accounts/${kind}?fileId=${fileId}`,
      "_blank"
    );
  };

  const handleClear = async () => {
    const ok = window.confirm(
      "This deletes the uploaded Excel accounts and resets the mapping for this file.\n\nContinue?"
    );
    if (!ok) return;
    try {
      setUploading(true);
      await api.delete(`/api/export/accounts/clear?fileId=${fileId}`);
      setFile(null);
      setUploaded(false);
      setMapped(false);
      toast.success("Mapping cleared", "Uploaded accounts were removed.");
    } catch (err) {
      toast.error("Could not clear mapping", errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      icon={SettingsIcon}
      iconTone="bg-indigo-50 text-indigo-600 ring-indigo-100"
      title="Account Mapping"
      description="Match your Excel chart of accounts against the accounts already in Naqood."
      size="lg"
      footer={
        <>
          <Button
            variant="danger"
            icon={TrashIcon}
            onClick={handleClear}
            className="mr-auto"
          >
            Clear mapping
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Dropzone */}
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f && !uploaded) setFile(f);
          }}
          onClick={() => !uploaded && inputRef.current?.click()}
          className={cx(
            "flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-9 text-center transition",
            uploaded
              ? "cursor-not-allowed border-emerald-200 bg-emerald-50/50"
              : dragging
              ? "cursor-pointer border-indigo-400 bg-indigo-50"
              : file
              ? "cursor-pointer border-indigo-300 bg-indigo-50/40"
              : "cursor-pointer border-ink-300 bg-ink-50/50 hover:border-indigo-300 hover:bg-indigo-50/30"
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xls,.xlsx"
            className="hidden"
            onChange={(e) => setFile(e.target.files[0])}
          />
          <span
            className={cx(
              "mb-3 grid h-11 w-11 place-items-center rounded-xl ring-1",
              uploaded
                ? "bg-emerald-100 text-emerald-600 ring-emerald-200"
                : file
                ? "bg-indigo-100 text-indigo-600 ring-indigo-200"
                : "bg-white text-ink-400 ring-ink-200"
            )}
          >
            {uploaded ? (
              <CheckIcon size={20} strokeWidth={2.5} />
            ) : file ? (
              <FileIcon size={20} />
            ) : (
              <UploadIcon size={20} />
            )}
          </span>
          <p className="text-[13.5px] font-medium text-ink-800">
            {uploaded
              ? "File uploaded"
              : file
              ? file.name
              : "Drop your Excel file here, or click to browse"}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-500">
            {uploaded ? "Run the mapping step below" : ".xls or .xlsx"}
          </p>
        </div>

        {/* Actions */}
        <div className="grid gap-3 sm:grid-cols-2">
          <Button
            icon={UploadIcon}
            onClick={handleUpload}
            loading={uploading}
            disabled={uploaded || !file}
            className="w-full"
          >
            {uploading ? "Uploading…" : uploaded ? "Uploaded" : "Upload Excel"}
          </Button>
          <Button
            variant="success"
            icon={SparkIcon}
            onClick={handleMapping}
            loading={mapping}
            disabled={!uploaded || mapped}
            className="w-full"
          >
            {mapping ? "Mapping…" : mapped ? "Mapped" : "Run mapping"}
          </Button>
        </div>

        {/* Downloads */}
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-ink-400">
            Download results
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Button
              variant="secondary"
              icon={DownloadIcon}
              disabled={!mapped}
              onClick={() => downloadExcel("account-export")}
              className="w-full"
            >
              Accounts Export
            </Button>
            <Button
              variant="secondary"
              icon={DownloadIcon}
              disabled={!mapped}
              onClick={() => downloadExcel("excel-accounts")}
              className="w-full"
            >
              Excel Accounts
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
